/**
 * MarkdownLite — renders the small Markdown subset the Help guides use
 * (src/help/*.md, Pedro 2026-10-11), without adding a Markdown dependency:
 *   # / ## / ### headings · paragraphs · `---` rules · `> ` notes
 *   `- ` and `1. ` lists (nested by indentation) · `| … |` tables
 *   inline **bold**, *italic*, `code`, and [text](https://…) links (Pedro 2026-10-13),
 *   which open in a new tab; only https links are rendered as links.
 * Anything else renders as plain text. Static, trusted content only (bundled at
 * build time) — it never renders user input, and it emits React elements, never HTML.
 */
import { Anchor, Blockquote, Code, Divider, Table, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';

// ── inline ──────────────────────────────────────────────────────────────────
const INLINE = /(\[[^\]]+\]\(https:\/\/[^)\s]+\)|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g;
const LINK = /^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/;

export function renderInline(text: string): ReactNode[] {
  return text.split(INLINE).filter((p) => p !== '').map((p, i) => {
    const link = LINK.exec(p);
    if (link) {
      return (
        <Anchor key={i} href={link[2]} target="_blank" rel="noopener noreferrer">
          {renderInline(link[1])}
        </Anchor>
      );
    }
    if (p.startsWith('**') && p.endsWith('**')) return <b key={i}>{renderInline(p.slice(2, -2))}</b>;
    if (p.startsWith('`') && p.endsWith('`')) return <Code key={i}>{p.slice(1, -1)}</Code>;
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <i key={i}>{renderInline(p.slice(1, -1))}</i>;
    return p;
  });
}

// ── blocks ──────────────────────────────────────────────────────────────────
export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

type ListItem = { indent: number; ordered: boolean; text: string; paras?: string[] };
const LIST_RX = /^(\s*)(-|\d+\.)\s+(.*)$/;

type ListNode = { text: string; paras: string[]; children: ListItem[] };

// Group a run of list items into the items at `indent`, each carrying the deeper
// items that follow it as its children (rendered as a nested list).
function renderList(items: ListItem[], key: string): ReactNode {
  const indent = items[0].indent;
  const nodes: ListNode[] = [];
  for (const it of items) {
    if (it.indent > indent && nodes.length > 0) nodes[nodes.length - 1].children.push(it);
    else nodes.push({ text: it.text, paras: it.paras ?? [], children: [] });
  }
  const style = { margin: '4px 0', paddingLeft: 22, fontSize: 'var(--mantine-font-size-sm)' };
  const lis = nodes.map((n, j) => (
    <li key={j}>
      {renderInline(n.text)}
      {n.children.length > 0 && renderList(n.children, `${key}-${j}`)}
      {n.paras.map((p, pj) => <Text key={pj} size="sm" my={4}>{renderInline(p)}</Text>)}
    </li>
  ));
  return items[0].ordered
    ? <ol key={key} style={style}>{lis}</ol>
    : <ul key={key} style={style}>{lis}</ul>;
}

// After a blank line, the list goes on if the next line is indented (a nested item or a
// continuation paragraph) or is an item of the same kind at the list's own level.
function continuesList(next: string, first: ListItem): boolean {
  const m = LIST_RX.exec(next);
  if (!m) return /^\s+\S/.test(next) && first.indent === 0;
  if (m[1].length > first.indent) return true;
  return m[1].length === first.indent && (m[2] !== '-') === first.ordered;
}

function splitRow(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

export function MarkdownLite({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const k = `b${i}`;
    if (line.trim() === '') { i += 1; continue; }
    if (line.trim() === '---') { out.push(<Divider key={k} my="sm" />); i += 1; continue; }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length as 1 | 2 | 3;
      out.push(
        <Title key={k} id={slugify(h[2])} order={level + 1 as 2 | 3 | 4} mt={level === 1 ? 0 : 'md'}>
          {renderInline(h[2])}
        </Title>,
      );
      i += 1;
      continue;
    }
    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) { quote.push(lines[i].replace(/^>\s?/, '')); i += 1; }
      out.push(
        <Blockquote key={k} color="blue" p="sm" my="xs">
          <Text size="sm">{renderInline(quote.join(' '))}</Text>
        </Blockquote>,
      );
      continue;
    }
    if (line.trim().startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        if (!/^\s*\|[\s|:-]+\|\s*$/.test(lines[i])) rows.push(splitRow(lines[i]));
        i += 1;
      }
      const [head, ...body] = rows;
      out.push(
        <Table key={k} withTableBorder withColumnBorders fz="sm" my="xs">
          <Table.Thead>
            <Table.Tr>{head.map((c, j) => <Table.Th key={j}>{renderInline(c)}</Table.Th>)}</Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {body.map((r, ri) => (
              <Table.Tr key={ri}>{r.map((c, j) => <Table.Td key={j}>{renderInline(c)}</Table.Td>)}</Table.Tr>
            ))}
          </Table.Tbody>
        </Table>,
      );
      continue;
    }
    if (LIST_RX.test(line)) {
      // A list runs on across blank lines and indented continuation paragraphs, so a
      // numbered list split by an explanatory paragraph keeps its numbering.
      const items: ListItem[] = [];
      while (i < lines.length) {
        const m = LIST_RX.exec(lines[i]);
        if (m) {
          items.push({ indent: m[1].length, ordered: m[2] !== '-', text: m[3] });
          i += 1;
          continue;
        }
        if (lines[i].trim() === '') {
          let j = i;
          while (j < lines.length && lines[j].trim() === '') j += 1;
          if (j < lines.length && continuesList(lines[j], items[0])) { i = j; continue; }
          break;
        }
        const cont = /^(\s+)\S/.exec(lines[i]);
        if (cont) {                                        // continuation paragraph:
          const owner = [...items].reverse().find((it) => it.indent < cont[1].length) ?? items[items.length - 1];
          owner.paras = [...(owner.paras ?? []), lines[i].trim()];   // belongs to the item it is indented under
          i += 1;
          continue;
        }
        break;
      }
      out.push(renderList(items, k));
      continue;
    }
    // paragraph: consecutive non-blank lines that start no other block
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,3}\s|>|\s*\||---$)/.test(lines[i])
      && !LIST_RX.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    out.push(<Text key={k} size="sm" my={4}>{para.map((p, j) => <span key={j}>{j > 0 && <br />}{renderInline(p)}</span>)}</Text>);
  }
  return <>{out}</>;
}
